/**
 * Main App component with routing
 */
import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import { Typography, CircularProgress } from '@mui/material';
import { QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { ReactFlowProvider } from 'reactflow';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { UserRole } from './types/user';
import Layout from './components/layout/Layout';
import EmployeeLayout from './components/layout/EmployeeLayout';

// Eagerly load only authentication pages
import Login from './pages/Login';
import EmergencyLogin from './pages/EmergencyLogin';  // Admin-only emergency login page
import ResetPassword from './pages/ResetPassword';
import ITEmailForm from './pages/ITEmailForm';
import ITOffboardingForm from './pages/ITOffboardingForm';
import QuickTerminateConfirm from './pages/QuickTerminateConfirm';

// Lazy load all other pages to reduce initial bundle size
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Employees = lazy(() => import('./pages/Employees'));
const Projects = lazy(() => import('./pages/Projects'));
const Onboarding = lazy(() => import('./pages/Onboarding'));
const Reports = lazy(() => import('./pages/Reports'));
const AllocationReports = lazy(() => import('./pages/AllocationReports'));
const UserManagement = lazy(() => import('./pages/UserManagement'));
const Profile = lazy(() => import('./pages/Profile'));
const OrganizationHierarchy = lazy(() => import('./pages/OrganizationHierarchy'));
const Clients = lazy(() => import('./pages/Clients'));

// ATS Pages - Lazy loaded
const ATSDashboard = lazy(() => import('./pages/ats/ATSDashboard'));
const OpenRoles = lazy(() => import('./pages/ats/OpenRoles'));
const Candidates = lazy(() => import('./pages/ats/Candidates'));
const Pipeline = lazy(() => import('./pages/ats/Pipeline'));
const CandidateDetail = lazy(() => import('./pages/ats/CandidateDetail'));
const CandidateInterviewHistory = lazy(() => import('./pages/ats/CandidateInterviewHistory'));
const RecentHires = lazy(() => import('./pages/RecentHires'));

// Employee Portal Pages - Lazy loaded
const EmployeeDashboard = lazy(() => import('./pages/EmployeeDashboard'));
const EmployeeProfile = lazy(() => import('./pages/EmployeeProfile'));
const Timesheets = lazy(() => import('./pages/Timesheets'));
const MyServiceRequests = lazy(() => import('./pages/MyServiceRequests'));
const ManagerApprovals = lazy(() => import('./pages/ManagerApprovals'));

// Admin Timesheet Management - Lazy loaded
const TimesheetApprovals = lazy(() => import('./pages/TimesheetApprovals'));
const Notifications = lazy(() => import('./pages/Notifications'));
const ServiceRequests = lazy(() => import('./pages/ServiceRequests'));

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,  // Don't refetch when window regains focus
      refetchOnMount: false,        // Don't refetch when component mounts if data exists
      refetchOnReconnect: false,    // Don't refetch when network reconnects
      retry: 1,
      staleTime: 60000,             // 1 minute - data stays fresh for current session
      gcTime: 5 * 60 * 1000,        // 5 minutes - keep in memory only for current session
    },
  },
});

// Create localStorage persister for ATS data persistence across page refreshes and navigation
const persister = createSyncStoragePersister({
  storage: window.localStorage,
  key: 'ats-cache',
  serialize: (data) => JSON.stringify(data),
  deserialize: (data) => JSON.parse(data),
});

// Simple loader shown while a lazy chunk is loading
const PageLoader = (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
    <CircularProgress />
  </Box>
);

// Protected route wrapper - currently unused, kept for future use
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Admin/HR only route wrapper (for EMS portal)
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // If user is an EMPLOYEE, redirect to employee portal
  if (user?.role === UserRole.EMPLOYEE) {
    return <Navigate to="/employee/dashboard" replace />;
  }

  return <>{children}</>;
};

// Employee only route wrapper (for Employee portal)
const EmployeeRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // If user is NOT an employee (Admin/HR/IT), redirect to admin dashboard
  if (user?.role && user.role !== UserRole.EMPLOYEE) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

// Public route wrapper (redirect to dashboard if already authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (isAuthenticated) {
    // Role-based redirect
    if (user?.role === UserRole.EMPLOYEE) {
      return <Navigate to="/employee/dashboard" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

function App() {
  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        maxAge: 24 * 60 * 60 * 1000, // 24 hours
        dehydrateOptions: {
          shouldDehydrateQuery: (query) => {
            // Persist all ATS queries to localStorage (dashboard, open roles, candidates, pipeline)
            const queryKey = query.queryKey[0];
            const isAtsQuery = typeof queryKey === 'string' && queryKey === 'ats';
            // Only persist successful queries with data
            const hasData = query.state.data !== undefined;
            const isSuccess = query.state.status === 'success';
            return isAtsQuery && hasData && isSuccess;
          },
        },
      }}
    >
      <ThemeProvider>
        <CssBaseline />
        <Router>
          <AuthProvider>
            <Suspense fallback={PageLoader}>
              <Routes>
                {/* Public routes */}
                <Route
                  path="/login"
                  element={
                    <PublicRoute>
                      <Login />
                    </PublicRoute>
                  }
                />

                {/* Emergency Login - Admin only, hidden page */}
                <Route path="/system-access" element={<EmergencyLogin />} />

                {/* Reset Password - Public, token-authenticated */}
                <Route path="/reset-password" element={<ResetPassword />} />

                {/* IT Email Form - Public, token-authenticated */}
                <Route path="/it-email-form/:token" element={<ITEmailForm />} />

                {/* IT Offboarding Form - Public, token-authenticated */}
                <Route path="/it-offboarding-form/:token" element={<ITOffboardingForm />} />

                {/* IT Quick Terminate Confirm - Public, token-authenticated */}
                <Route path="/it-confirm-access/:token" element={<QuickTerminateConfirm />} />

                {/* Protected routes */}
                <Route
                  path="/"
                  element={
                    <AdminRoute>
                      <Layout />
                    </AdminRoute>
                  }
                >
                  <Route index element={<Navigate to="/dashboard" replace />} />
                  <Route path="dashboard" element={<Dashboard />} />
                  <Route path="employees" element={<Employees />} />
                  <Route path="projects" element={<Projects />} />
                  <Route path="clients" element={<Clients />} />
                  <Route path="onboarding" element={<Onboarding />} />
                  <Route path="offboarding" element={<Navigate to="/onboarding" replace />} />
                  <Route path="timesheets" element={<TimesheetApprovals />} />
                  <Route path="notifications" element={<Notifications />} />
                  <Route path="reports" element={<Reports />} />
                  <Route path="allocation-reports" element={<AllocationReports />} />
                  <Route path="users" element={<UserManagement />} />
                  <Route path="profile" element={<Profile />} />
                  <Route path="service-requests" element={<ServiceRequests />} />
                  <Route
                    path="organization"
                    element={
                      <ReactFlowProvider>
                        <OrganizationHierarchy />
                      </ReactFlowProvider>
                    }
                  />

                  {/* ATS Routes */}
                  <Route path="ats" element={<ATSDashboard />} />
                  <Route path="ats/open-roles" element={<OpenRoles />} />
                  <Route path="ats/candidates" element={<Candidates />} />
                  <Route path="ats/recent-hires" element={<RecentHires />} />
                  <Route path="ats/open-roles/:roleId/pipeline" element={<Pipeline />} />
                  <Route path="ats/candidates/:candidateId" element={<CandidateDetail />} />
                  <Route path="ats/candidates/:candidateId/interview-history" element={<CandidateInterviewHistory />} />
                </Route>

                {/* Employee Portal Routes */}
                <Route
                  path="/employee"
                  element={
                    <EmployeeRoute>
                      <EmployeeLayout />
                    </EmployeeRoute>
                  }
                >
                  <Route index element={<Navigate to="/employee/dashboard" replace />} />
                  <Route path="dashboard" element={<EmployeeDashboard />} />
                  <Route path="profile" element={<EmployeeProfile />} />
                  <Route path="timesheets" element={<Timesheets />} />
                  <Route path="leave" element={<Box sx={{ p: 3 }}><Typography>Leave Management - Coming Soon</Typography></Box>} />
                  <Route path="approvals" element={<ManagerApprovals />} />
                  <Route path="service-requests" element={<MyServiceRequests />} />
                  <Route
                    path="organization"
                    element={
                      <ReactFlowProvider>
                        <OrganizationHierarchy />
                      </ReactFlowProvider>
                    }
                  />
                  <Route path="notifications" element={<Box sx={{ p: 3 }}><Typography>Notifications - Coming Soon</Typography></Box>} />
                </Route>

                {/* 404 */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </AuthProvider>
        </Router>
      </ThemeProvider>
    </PersistQueryClientProvider>
  );
}

export default App;
